import { useState, useCallback, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { ProjectDetailsCard } from "../components/ProjectDetailsCard";
import { RiskTicker } from "../components/RiskTicker";
import { KpiCards } from "../components/KpiCards";
import { useLiveProgressMap } from "../hooks/useLiveProgressMap";
import { useLiveProgressRisks } from "../hooks/useLiveProgressRisks";
import { useLiveProgressProjects, Project } from "../hooks/useLiveProgressProjects";

export const LiveProgress = () => {
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [showConstructionOnly, setShowConstructionOnly] = useState(false);

  const {
    projects,
    isLoading: isLoadingProjects,
    isError: isProjectsError,
    error: projectsError,
  } = useLiveProgressProjects({
    limit: 100,
    is_construction: showConstructionOnly ? "true" : "false",
  });

  const handleProjectChange = useCallback((project: Project | null) => {
    setCurrentProject(project);
  }, []);

  const {
    mapContainer,
    isLoading: isMapLoading,
    error: mapError,
  } = useLiveProgressMap({
    projects,
    onProjectChange: handleProjectChange,
  });

  const {
    risks,
    isLoading: isLoadingRisks,
    isError: isRisksError,
    error: risksError,
  } = useLiveProgressRisks({
    is_construction: showConstructionOnly ? "true" : "false",
  });

  useEffect(() => {
    if (currentProject && !projects.find((p) => p.id === currentProject.id)) {
      setCurrentProject(null);
    }
  }, [projects, currentProject]);

  if (mapError || isProjectsError) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          flexDirection: "column",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            {mapError ? "Map Error" : "Error Loading Projects"}
          </Typography>
          <Typography variant="body2">
            {mapError || projectsError || "Failed to load data"}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {(isMapLoading || isLoadingProjects) && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            zIndex: 2000,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading live project progress...
          </Typography>
        </Box>
      )}

      <Box
        ref={mapContainer}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          "& .mapboxgl-ctrl-logo": {
            display: "none",
          },
          "& .mapboxgl-popup-content": {
            bgcolor: "background.paper",
            color: "text.primary",
            "& .MuiTypography-root": {
              color: "text.primary",
            },
          },
          "& .mapboxgl-popup-close-button": {
            color: "text.primary",
          },
          "& .mapboxgl-popup-anchor-top .mapboxgl-popup-tip, & .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip, & .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip": {
            borderBottomColor: "background.paper",
          },
          "& .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip, & .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip, & .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip": {
            borderTopColor: "background.paper",
          },
          "& .mapboxgl-popup-anchor-left .mapboxgl-popup-tip": {
            borderRightColor: "background.paper",
          },
          "& .mapboxgl-popup-anchor-right .mapboxgl-popup-tip": {
            borderLeftColor: "background.paper",
          },
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          px: 2,
          pt: 2,
        }}
      >
        <KpiCards is_construction={showConstructionOnly ? "true" : "false"} />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          px: 2,
          pt: 2,
          width: { xs: "100%", sm: 400, md: 420 },
          flexShrink: 0,
        }}
      >
        <ProjectDetailsCard
          project={
            currentProject
              ? {
                  ...currentProject,
                  budget: currentProject.total_budget,
                  spent: currentProject.total_spent || 0,
                  remaining: currentProject.total_remaining || 0,
                  start_date: currentProject.planned_start_date,
                  end_date: currentProject.planned_end_date,
                }
              : null
          }
          isLoading={false}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 112, // Above the RiskTicker
          right: 16,
          zIndex: 10,
          bgcolor: "background.paper",
          p: "2px 12px 2px 6px",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={showConstructionOnly}
              onChange={(e) => setShowConstructionOnly(e.target.checked)}
              disabled={isLoadingProjects}
            />
          }
          label="Construction Phase"
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          px: 2,
          pb: 2,
        }}
      >
        <RiskTicker risks={risks} isLoading={isLoadingRisks} />
      </Box>

      {projects.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 20,
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Projects Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no projects with location data to display on the map.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
